import { useState, useCallback, useMemo } from "react";
import BreakdownDialog from "./BreakdownDialog";
import { getInvoiceStatusPillStyle } from "./invoiceStatusStyle";

export const INVOICE_VOLUME_VALUE_BREAKDOWN_COLUMNS = [
  { key: "invoice_number", label: "Invoice No." },
  { key: "invoice_type", label: "Invoice Type" },
  { key: "status", label: "Status", type: "pill" },
  { key: "lessor", label: "Lessor" },
  { key: "lessee", label: "Lessee" },
  { key: "due_date", label: "Due Date", type: "date" },
  { key: "amount", label: "Amount", mono: true },
];

function formatInvoiceAmount(value, currency) {
  const amount = typeof value === "number" ? value : parseFloat(value);
  if (!Number.isFinite(amount)) return "-";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return amount.toFixed(2);
  }
}

// Each breakdown entry is a (period, invoice_type) bucket carrying the actual
// invoices that landed in it; flatten those out into individual rows.
function buildInvoiceVolumeValueBreakdownRows(entries) {
  if (!Array.isArray(entries)) return [];
  const rows = [];
  entries.forEach((entry, entryIndex) => {
    const invoices = Array.isArray(entry?.invoices) ? entry.invoices : [];
    invoices.forEach((invoice, invoiceIndex) => {
      rows.push({
        id: invoice?.id ?? invoice?.invoice_number ?? `${entryIndex}-${invoiceIndex}`,
        invoice_number: invoice?.invoice_number || "-",
        invoice_type: entry?.invoice_type || "-",
        status: { label: invoice?.status || "-", style: getInvoiceStatusPillStyle(invoice?.status) },
        lessor: invoice?.lessor || "Unassigned",
        lessee: invoice?.lessee || "Unassigned",
        due_date: invoice?.due_date,
        amount: formatInvoiceAmount(invoice?.amount, invoice?.currency || entry?.currency),
        _period: entry?.period || "Unknown",
      });
    });
  });
  return rows;
}

// Drives the "Invoice Volume & Value by Type" popup: clicking a stacked bar
// (any segment within it) filters the breakdown down to that week's period,
// showing every underlying invoice across all invoice types in that week.
export function useInvoiceVolumeValueByTypeBreakdown({ breakdown, onTrack }) {
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const reset = useCallback(() => {
    setSelectedPeriod(null);
    setModalOpen(false);
  }, []);

  const allRows = useMemo(() => buildInvoiceVolumeValueBreakdownRows(breakdown), [breakdown]);
  const canRender = allRows.length > 0;

  const rows = useMemo(() => {
    if (!selectedPeriod) return allRows;
    return allRows.filter((row) => row._period === selectedPeriod);
  }, [allRows, selectedPeriod]);

  const handleChartClick = useCallback(
    (params) => {
      if (!params || params.componentType !== "series") return;
      const period = params.name || null;
      setSelectedPeriod(period);
      setModalOpen(true);
      onTrack?.(`Graph Bar Clicked: ${period || ""}`);
    },
    [onTrack],
  );

  const chartEvents = useMemo(() => ({ click: handleChartClick }), [handleChartClick]);

  const closeModal = useCallback(() => setModalOpen(false), []);

  const titleSuffix = useMemo(() => selectedPeriod || "", [selectedPeriod]);

  return { rows, canRender, chartEvents, modalOpen, closeModal, titleSuffix, reset };
}

export function InvoiceVolumeValueByTypeBreakdownModal({ open, onClose, rows, titleSuffix }) {
  const title = "Invoice Volume & Value by Type";
  return (
    <BreakdownDialog
      open={open}
      onClose={onClose}
      title={title}
      subtitle={titleSuffix}
      columns={INVOICE_VOLUME_VALUE_BREAKDOWN_COLUMNS}
      rows={rows}
    />
  );
}
